import { ApiError, del, get, patch, post, type Me } from '../api.js';
import {
  button, chip, clear, el, empty, field, input, panel, select, skeleton, table, toast,
} from '../ui.js';
import {
  PRODUCT_TYPES, PRODUCT_TYPE_LABEL, type ProductType,
} from '../../../../packages/core/src/policies.js';

/**
 * What cases pay.
 *
 * WHY THIS SCREEN IS THE ONE THAT MAKES THE REST OF THE PRODUCT MEAN ANYTHING
 * Until it existed, every money figure in the platform was null. The scoreboard
 * could report that an agent had written eleven cases and forty thousand
 * dollars of annualised premium and could not say what any of it paid them,
 * because the commission rate was a field on each individual case that somebody
 * had to type. Nobody typed it. So the one thing an agent actually wants from a
 * CRM — what did I earn — was the one thing it could not answer.
 *
 * A dozen rows here price every case the agency will ever write.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * Suggest a rate. There is no "typical final expense contract pays 110%"
 * anywhere on this screen and there never will be, because a number that
 * appears without somebody having entered it will be believed, and this is the
 * number an agent works out their mortgage against.
 */

interface Rule {
  id: string;
  carrierId: string | null;
  productType: ProductType | null;
  userId: string | null;
  rateBps: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  label: string | null;
  /** Null means nobody has checked this against a carrier schedule. */
  verifiedAt: string | null;
  sourceNote: string | null;
}

interface Grid {
  rules: Rule[];
  conflicts: Array<{ a: string; b: string; describes: string }>;
  unpricedCases: number;
  pricedCases: number;
  uncheckedRules: number;
}

interface Band { cases: number; expectedCents: number; paidCents: number; gapCents: number }

interface Gap {
  windowMonths: number;
  tolerancePct: number;
  short: Band; pending: Band; over: Band; matched: Band; unpaid: Band;
  withoutRate: number;
  worst: Array<{
    policyId: string; insuredName: string | null; agentName: string | null;
    carrierName: string | null; expectedCents: number; paidCents: number;
    gapCents: number; monthsInForce: number | null; why: string | null;
  }>;
}

interface Carrier { id: string; name: string }
interface Person { id: string; firstName: string; lastName: string }

const ANY = '';

/** 11000 → "110%", 9550 → "95.5%". Never a bare basis-point figure on screen. */
const asPercent = (bps: number): string =>
  `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2).replace(/\.?0+$/, '')}%`;

const asDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

/** The whole grid panel, for the settings screen to drop in. */
export function commissionPanel(me: Me): HTMLElement {
  const host = el('div');
  void render(host, me);
  return host;
}

async function render(host: HTMLElement, me: Me): Promise<void> {
  clear(host);
  host.append(skeleton(4));

  let grid: Grid;
  let carriers: Carrier[] = [];
  let people: Person[] = [];
  try {
    /*
     * All three at once. The grid editor needs carrier names and people's names
     * to say anything readable, and fetching them one after another is three
     * round trips to draw one table.
     */
    const [g, cs, ps] = await Promise.all([
      get<Grid>('/v1/commission/grid'),
      get<{ items: Carrier[] } | Carrier[]>('/v1/carriers'),
      get<{ items: Person[] } | Person[]>('/v1/users'),
    ]);
    grid = g;
    carriers = Array.isArray(cs) ? cs : cs.items ?? [];
    people = Array.isArray(ps) ? ps : ps.items ?? [];
  } catch (e) {
    clear(host);
    host.append(el('div', { class: 'warnbox' },
      el('b', null, 'The commission grid could not be loaded. '),
      e instanceof ApiError ? e.message : 'Something went wrong.'));
    return;
  }

  const carrierName = new Map(carriers.map((c) => [c.id, c.name]));
  const personName = new Map(people.map((p) => [p.id, `${p.firstName} ${p.lastName}`.trim()]));
  const mayEdit = me.capabilities.includes('org.manage');
  const reload = (): void => { void render(host, me); };

  clear(host);

  /* ------------------------------------------------ what it is, in words -- */

  host.append(el('p', { class: 'note-p' },
    el('b', null, 'This is what a case pays, so nobody has to type a rate on one. '),
    'A row applies to every case it matches — most specific first: a row naming an '
    + 'agent beats one naming a kind of policy, which beats one naming a carrier, '
    + 'which beats the agency default. Leave a field on "any" to make the row broader.'));

  /*
   * THE TRAP THIS BOX EXISTS FOR.
   *
   * "I am on a 110 contract" is what a producer actually knows, and it is NOT a
   * commission percentage — at least one carrier's own field guide says outright
   * that the contract level number "does not correspond to a specific commission
   * percentage". Somebody who types their level into a box labelled "pays %"
   * gets a grid that is confidently wrong, and confidently wrong is worse than
   * empty because they will act on it.
   *
   * Said once, at the top, where the number is about to be typed — not buried
   * in help nobody opens.
   */
  host.append(el('div', { class: 'note' },
    el('b', null, 'A contract level is not a commission percentage. '),
    'Being "on a 110" is a position in a hierarchy; what goes in the box below is the '
    + 'first-year commission as a percentage of target premium, off a carrier\'s comp '
    + 'schedule. If you do not have the schedule yet, put in what you believe it is — '
    + 'everything works — and it stays marked unchecked until somebody confirms it '
    + 'against the document.'));

  /* --------------------------------------------- what it can and cannot do -- */

  const total = grid.pricedCases + grid.unpricedCases;
  if (total > 0) {
    const share = Math.round((grid.pricedCases / total) * 100);
    host.append(el('div', { class: grid.unpricedCases > 0 ? 'warnbox' : 'okbox' },
      grid.unpricedCases > 0
        ? el('span', null,
          el('b', null, `${grid.unpricedCases} of your ${total} cases have no rate. `),
          'They are left out of every commission total on the scoreboard and the '
          + 'chargeback report — not counted as zero, left out, because a total that '
          + 'quietly excluded part of the book would be worse than no total. '
          + (grid.rules.length === 0
            ? 'Adding one row with everything set to "any" prices all of them.'
            : 'A row with everything set to "any" would catch whatever the others miss.'))
        : el('span', null,
          el('b', null, 'Every case has a rate. '),
          `All ${total} are priced from this grid or from a rate entered on the case itself.`)));
    if (grid.unpricedCases > 0 && share > 0) {
      host.append(el('div', { class: 'qsub', style: 'margin-top:6px' },
        `${share}% of the book is priced.`));
    }
  }

  if (grid.uncheckedRules > 0) {
    host.append(el('div', { class: 'warnbox', style: 'margin-top:8px' },
      el('b', null, `${grid.uncheckedRules} rate${grid.uncheckedRules === 1 ? '' : 's'} `
        + 'here nobody has checked against a carrier schedule. '),
      'They are being used — every case they match is priced from them — and every figure '
      + 'that comes out of them carries this same warning until somebody confirms it. '
      + 'A remembered number prices a book exactly as confidently as a documented one.'));
  }

  for (const c of grid.conflicts) {
    /*
     * Said out loud rather than refused at save time, because the ordinary way
     * this happens is somebody correcting a date on a row that briefly overlaps
     * another. The resolver picks one deterministically — which is not the same
     * as it being what anybody meant, and the silent version of this is an
     * agency paying half its book on a row nobody knew was winning.
     */
    host.append(el('div', { class: 'warnbox', style: 'margin-top:8px' },
      el('b', null, 'Two rows overlap. '), c.describes,
      '. One of them is being used and the other is not; end one of their dates so it is '
      + 'clear which.'));
  }

  /* ------------------------------------------------------------ the rows -- */

  if (!grid.rules.length) {
    host.append(empty('No rates entered',
      mayEdit
        ? 'Until there is at least one row here, the platform cannot say what any case pays '
          + 'and every commission column stays empty. One row — everything set to "any", with '
          + 'your contract percentage — is enough to start.'
        : 'Nobody has entered what cases pay yet. An owner or administrator sets this up.'));
  } else {
    host.append(table(
      ['Rate', 'Checked', 'Carrier', 'Kind of policy', 'Agent', 'From', 'Until', 'Note', ''],
      grid.rules.map((r) => ({
        cells: [
          el('b', { text: asPercent(r.rateBps) }),
          r.verifiedAt
            ? chip('checked', 'ok')
            : el('span', {
              class: 'qsub',
              title: 'Nobody has confirmed this against a carrier comp schedule.',
              text: 'not checked',
            }),
          r.carrierId ? carrierName.get(r.carrierId) ?? 'a carrier that has gone' : chip('any'),
          r.productType ? PRODUCT_TYPE_LABEL[r.productType] : chip('any'),
          r.userId ? personName.get(r.userId) ?? 'somebody who has left' : chip('any'),
          asDate(r.effectiveFrom),
          r.effectiveTo ? asDate(r.effectiveTo) : chip('still applies', 'ok'),
          r.sourceNote ? `${r.label ?? ''}${r.label ? ' — ' : ''}${r.sourceNote}` : r.label ?? '',
          mayEdit
            ? el('div', { style: 'display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap' },
              r.verifiedAt
                ? button('Un-check', 'sm', () => void setChecked(r, false, reload))
                : button('I checked it', 'sm pri', () => void setChecked(r, true, reload)),
              button('End it', 'sm', () => void endRow(r, reload)),
              button('Remove', 'sm danger', () => void removeRow(r, reload)))
            : '',
        ],
      })),
    ));
  }

  /* ----------------------------------------------------------- adding one -- */

  /*
   * Before the early return below, because an agent sees this too — of their own
   * cases only, which the server decides. An agent who cannot check whether they
   * were paid what their own contract says has to ask somebody, and this is the
   * one number they would most want to check for themselves.
   */
  host.append(gapPanel());

  if (!mayEdit) {
    host.append(el('div', { class: 'qsub', style: 'margin-top:12px' },
      'You can see what cases pay. Only an owner or administrator can change it.'));
    return;
  }

  host.append(addForm(carriers, people, reload));
}

const gapMoney = (cents: number): string =>
  `$${Math.abs(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/**
 * What arrived, against what the grid says was owed.
 *
 * WHY IT IS ON THIS SCREEN
 * Because it is the other half of the same sentence. The rows above say what a
 * case pays; this says what the carrier actually sent. Nothing else in this
 * platform — or in most platforms — ever puts those two numbers next to each
 * other, which is why a case paid eight hundred dollars against a contract that
 * says thirteen hundred is filed and never thought about again.
 *
 * WHY IT IS CAREFUL ABOUT WHAT IT CLAIMS
 * A life commission arrives in instalments. A case short today may simply not
 * have finished paying, and telling an agent they were underpaid when they were
 * not sends them to argue with a carrier about money that is not due yet. Being
 * wrong about that once is enough for them never to check again — so the
 * heading says "worth checking", the advance window it assumes is printed on
 * the screen, and cases still inside that window are shown separately and
 * called ordinary.
 */
function gapPanel(): HTMLElement {
  const host = el('div');
  void (async () => {
    host.append(skeleton(2));
    let g: Gap;
    try {
      g = await get<Gap>('/v1/commission/gap');
    } catch {
      clear(host);
      return;
    }
    clear(host);

    const totalCases = g.short.cases + g.pending.cases + g.over.cases
      + g.matched.cases + g.unpaid.cases;
    if (totalCases === 0 && g.withoutRate === 0) return;

    const rows: HTMLElement[] = [];

    if (g.short.cases > 0) {
      rows.push(el('div', { class: 'warnbox' },
        el('b', null, `${g.short.cases} case${g.short.cases === 1 ? '' : 's'} `
          + `paid ${gapMoney(g.short.gapCents)} less than your grid says they owe. `),
        `All of them placed more than ${g.windowMonths} months ago, so they have had time to `
        + 'finish paying. That does not prove anybody was shorted — it means these are the '
        + 'ones worth pulling the statement on.'));
    }

    if (g.over.cases > 0) {
      rows.push(el('div', { class: 'warnbox' },
        el('b', null, `${g.over.cases} case${g.over.cases === 1 ? '' : 's'} `
          + `paid ${gapMoney(g.over.gapCents)} MORE than expected. `),
        'That is not good news. An overpayment is somebody else\'s mistake and it usually '
        + 'comes back as a chargeback, months later, after it has been spent.'));
    }

    if (g.short.cases === 0 && g.over.cases === 0 && g.matched.cases > 0) {
      rows.push(el('div', { class: 'okbox' },
        el('b', null, `Every settled case was paid what it should have been. `),
        `${g.matched.cases} checked, within ${g.tolerancePct}%.`));
    }

    const tiles = el('div', { class: 'tiles' },
      tileLine(String(g.matched.cases), 'Paid as expected', `within ${g.tolerancePct}%`, 'var(--good)'),
      tileLine(String(g.short.cases), 'Short, and settled',
        g.short.cases ? gapMoney(g.short.gapCents) : 'nothing outstanding', 'var(--crit)'),
      tileLine(String(g.pending.cases), 'Short, still in window',
        `under ${g.windowMonths} months — ordinary`, 'var(--ink-4)'),
      tileLine(String(g.unpaid.cases), 'Placed, nothing paid',
        g.unpaid.cases ? 'on your call list' : 'none', 'var(--copper)'));

    const worst = g.worst.length
      ? table(['Insured', 'Carrier', 'Agent', 'Expected', 'Paid', 'Short by', 'Age'],
        g.worst.slice(0, 15).map((w) => ({
          cells: [
            w.insuredName ?? '—',
            w.carrierName ?? '—',
            w.agentName ?? '—',
            gapMoney(w.expectedCents),
            gapMoney(w.paidCents),
            el('b', { style: 'color:var(--crit)', text: gapMoney(w.gapCents) }),
            w.monthsInForce === null ? '—' : `${w.monthsInForce} mo`,
          ],
        })))
      : null;

    host.append(panel('What arrived, against what was owed',
      el('p', { class: 'note-p' },
        'Cases are compared against the grid above. A commission arrives in instalments, so '
        + `anything placed less than ${g.windowMonths} months ago is expected to be short and is `
        + `counted separately. Differences under ${g.tolerancePct}% are treated as rounding.`),
      ...rows,
      tiles,
      worst,
      g.withoutRate > 0
        ? el('div', { class: 'qsub', style: 'margin-top:10px' },
          `${g.withoutRate} placed case${g.withoutRate === 1 ? '' : 's'} left out entirely, because `
          + 'the grid has nothing to say about them. They are not counted as paid or unpaid.')
        : null));
  })();
  return host;
}

/** A tile, without importing the one the dialling screen builds inline. */
const tileLine = (
  value: string, label: string, note: string | null, accent: string,
): HTMLElement => el('div', { class: 'tile', style: `--accent:${accent}` },
  el('div', { class: 'k', text: label }),
  el('div', { class: 'v', text: value }),
  note ? el('div', { class: 'd', text: note }) : null);

/**
 * "End it" rather than "edit the date".
 *
 * A contract change is not a correction — the old rate was right for everything
 * written under it, and overwriting it would move money that has already been
 * reported. So the ordinary action is to close a row as of today and add the
 * new one, which leaves both in the record.
 */
/**
 * Somebody putting their name to a rate, against a document.
 *
 * It asks WHICH document and refuses without one, because a rate marked checked
 * with no source is exactly as unchecked as one nobody looked at — and it now
 * carries a green chip saying otherwise, which is worse than the warning it
 * replaced.
 */
async function setChecked(r: Rule, verified: boolean, reload: () => void): Promise<void> {
  let sourceNote: string | null = null;
  if (verified) {
    sourceNote = prompt(
      `Which document did you check ${asPercent(r.rateBps)} against?\n\n`
      + 'A schedule name and date is enough — "MoO FE comp schedule, Aug 2026".',
    );
    if (!sourceNote || !sourceNote.trim()) return;
  }
  try {
    await post(`/v1/commission/rules/${r.id}/verify`, {
      verified, ...(sourceNote ? { sourceNote: sourceNote.trim() } : {}),
    });
    toast(verified ? 'Marked as checked.' : 'Marked as not checked.', 'ok');
    reload();
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Could not record that.', 'crit');
  }
}

async function endRow(r: Rule, reload: () => void): Promise<void> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!confirm(`Stop applying ${asPercent(r.rateBps)} from today?\n\n`
    + 'Cases that already placed under it keep paying it. Anything that places from now on '
    + 'falls to whichever other row matches — which may be none.')) return;
  try {
    await patch(`/v1/commission/rules/${r.id}`, { effectiveTo: today.toISOString() });
    toast('Ended. Cases placed under it are unaffected.', 'ok');
    reload();
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Could not end that rate.', 'crit');
  }
}

async function removeRow(r: Rule, reload: () => void): Promise<void> {
  if (!confirm(`Remove ${asPercent(r.rateBps)} from the grid entirely?\n\n`
    + 'Cases that already stamped this rate keep it — the money does not change. What is '
    + 'lost is the record of WHICH row paid them. Ending it instead keeps that.')) return;
  try {
    await del(`/v1/commission/rules/${r.id}`);
    toast('Removed.', 'ok');
    reload();
  } catch (e) {
    toast(e instanceof ApiError ? e.message : 'Could not remove that rate.', 'crit');
  }
}

function addForm(carriers: Carrier[], people: Person[], reload: () => void): HTMLElement {
  /*
   * A percentage, not basis points. Nobody in an agency has ever said "eleven
   * thousand basis points" out loud, and a field that demands it produces a
   * 110 meaning 1.1% at least once per agency.
   */
  const rate = input({ type: 'number', min: '0', max: '300', step: '0.5', placeholder: '110' });
  const carrier = select(
    [[ANY, 'Any carrier'], ...carriers.map((c) => [c.id, c.name] as [string, string])], ANY,
  );
  const kind = select(
    [[ANY, 'Any kind of policy'],
      ...PRODUCT_TYPES.map((t) => [t, PRODUCT_TYPE_LABEL[t]] as [string, string])], ANY,
  );
  const who = select(
    [[ANY, 'Any agent'],
      ...people.map((p) => [p.id, `${p.firstName} ${p.lastName}`.trim()] as [string, string])], ANY,
  );
  const from = input({ type: 'date' });
  const label = input({ placeholder: 'Loyalty 110 contract', maxLength: '120' });
  const source = input({ placeholder: 'MoO FE schedule, Aug 2026', maxLength: '300' });

  const add = button('Add this rate', 'pri', async () => {
    const pct = Number(rate.value);
    if (!rate.value || !Number.isFinite(pct) || pct < 0) {
      toast('Put in the percentage this pays.', 'info');
      rate.focus();
      return;
    }
    if (pct > 300) {
      toast('That is over 300%. If you meant basis points, enter the percentage instead.', 'crit');
      return;
    }
    add.disabled = true;
    try {
      await post('/v1/commission/rules', {
        // One multiplication, here, and the wire carries basis points from then
        // on so nothing is rounded a second time.
        rateBps: Math.round(pct * 100),
        ...(carrier.value ? { carrierId: carrier.value } : {}),
        ...(kind.value ? { productType: kind.value } : {}),
        ...(who.value ? { userId: who.value } : {}),
        ...(from.value ? { effectiveFrom: new Date(from.value).toISOString() } : {}),
        ...(label.value.trim() ? { label: label.value.trim() } : {}),
        ...(source.value.trim() ? { sourceNote: source.value.trim() } : {}),
      });
      toast('Added. Every case it matches is priced from now on.', 'ok');
      reload();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not add that rate.', 'crit');
      add.disabled = false;
    }
  });

  return panel('Add a rate',
    el('div', { class: 'row3' },
      field('Pays (%)', rate, 'Of target premium where there is one, otherwise annual premium.'),
      field('Carrier', carrier, 'Leave on "any" to cover every carrier.'),
      field('Kind of policy', kind, 'Leave on "any" to cover everything they write.')),
    el('div', { class: 'row3', style: 'margin-top:12px' },
      field('Agent', who, 'A row naming somebody beats every broader row, for them.'),
      field('From', from, 'Blank means today. Cases placed before this keep their old rate.'),
      field('Note', label, 'Your words for this row. Shown wherever the rate is used.')),
    el('div', { class: 'row2', style: 'margin-top:12px' },
      field('Which document says so', source,
        'Leave blank if you do not have the schedule yet — the rate still works, and stays '
        + 'marked unchecked.')),
    el('div', { style: 'margin-top:14px' }, add));
}
